import { useState } from "react";
import { useAnp } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { AnpDto, SalvarAnpRequest } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Fuel, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── Formulário vazio ───────────────────────────────────────────────────────

const EMPTY: SalvarAnpRequest = {
  codigo:           "",
  descricaoProduto: "",
  isCombustivel:    false,
  percentualMix:    null,
};

// ── Página ────────────────────────────────────────────────────────────────

export default function AnpCombustivel() {
  const [busca, setBusca]           = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarAnpRequest>(EMPTY);
  const [saving, setSaving]         = useState(false);

  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;

  const { query, obterPorId, criar, atualizar, excluir } = useAnp(busca || undefined, pagina, tamanhoPagina);
  const itens = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarAnpRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    try {
      const dto: AnpDto = await obterPorId(id);
      setEditId(id);
      setForm({
        codigo:           dto.codigo ?? "",
        descricaoProduto: dto.descricaoProduto ?? "",
        isCombustivel:    dto.isCombustivel,
        percentualMix:    dto.percentualMix,
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar produto ANP.");
    }
  };

  const handleSave = async () => {
    if (!form.codigo?.trim()) { toast.error("Código é obrigatório."); return; }
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("Produto ANP atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Produto ANP criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar produto ANP.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("Produto ANP excluído.");
    } catch {
      toast.error("Erro ao excluir produto ANP.");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Fuel className="h-6 w-6" /> ANP — Combustíveis
          </h1>
          <p className="text-muted-foreground">Agência Nacional do Petróleo — tabela de produtos</p>
        </div>
        {podeEditar && (
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Novo</Button>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por código ou descrição..."
          className="pl-9"
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Código ANP</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center w-[120px]">Combustível</TableHead>
              <TableHead className="text-right w-[100px]">% Mix</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum produto ANP encontrado.</TableCell></TableRow>
            ) : itens.map(item => (
              <TableRow key={item.id}>
                <TableCell className="font-mono font-semibold">{item.codigo ?? "—"}</TableCell>
                <TableCell className="text-sm">{item.descricaoProduto ?? "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.isCombustivel ? "default" : "secondary"}>
                    {item.isCombustivel ? "Sim" : "Não"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">
                  {item.percentualMix != null ? `${item.percentualMix.toFixed(2)}%` : "—"}
                </TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(item.id)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Editar Produto ANP" : "Novo Produto ANP"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Código ANP <span className="text-destructive">*</span></Label>
              <Input
                value={form.codigo ?? ""}
                onChange={e => set("codigo", e.target.value)}
                maxLength={80}
                placeholder="Código ANP"
              />
            </div>
            <div className="space-y-1">
              <Label>Descrição do Produto</Label>
              <Input
                value={form.descricaoProduto ?? ""}
                onChange={e => set("descricaoProduto", e.target.value)}
                maxLength={40}
                placeholder="Descrição"
              />
            </div>
            <div className="space-y-1">
              <Label>% Mix</Label>
              <Input
                type="number"
                step="0.01"
                value={form.percentualMix ?? ""}
                onChange={e => set("percentualMix", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="0,00"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isCombustivel"
                checked={form.isCombustivel}
                onCheckedChange={v => set("isCombustivel", !!v)}
              />
              <label htmlFor="isCombustivel" className="text-sm cursor-pointer">Combustível</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Produto ANP?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
